import axios, {
  AxiosError,
  AxiosRequestConfig,
  AxiosPromise,
  AxiosResponse
} from 'axios'
import { ecp } from '../utils/rangy'
import qs from 'qs'
import { isEmpty, merge } from 'xe-utils'
import { useLoginStore } from '../store'

export interface ApiResponse<T = {}> {
  data: T
  code: number
  message: string
}

export const enum ContentType {
  form = 'application/x-www-form-urlencoded',
  json = 'application/json; charset=utf-8',
  multipart = 'multipart/form-data'
}

/**
 * 将对象转为 formdate 格式
 * @param {*} obj 传入纯对象
 */
export const tansObjToFormdate = (
  obj: { [key: number | string]: any } | FormData
): FormData => {
  let fdInstance: FormData
  if (!(obj instanceof FormData)) {
    fdInstance = new FormData()
    for (let [key, value] of Object.entries(obj)) {
      if (Array.isArray(value)) {
        for (let i of value) {
          fdInstance.append(key, i)
        }
      } else {
        fdInstance.append(key, value)
      }
    }
  } else {
    fdInstance = obj
  }
  return fdInstance
}

export const delay = (ms: number) => {
  return new Promise((resolve) => {
    setTimeout(resolve, ms)
  })
}

export class BaseService {
  prefix = '/api' as const

  streamPrefix = '/stream' as const

  isSuccess(res: ApiResponse) {
    return res && res.code === 0
  }
}

const constants = {
  maxRetry: 2,
  initialRetryInterval: 5000
}

const isTransient = (err: AxiosError) => {
  console.log(err)
  if (err.code == 'ECONNABORTED' && err.message.indexOf('timeout') != -1)
    return true // 超时
  if (err && err.response) {
    return [408, 429, 444, 500, 503, 504].includes(err.response.status)
  }
  return false
}

const sendRequestWithAutoRetry = async <T>(
  request: () => AxiosPromise<T>,
  maxRetry = constants.maxRetry
) => {
  let currentRetry = 0
  while (currentRetry <= maxRetry) {
    try {
      return await request()
    } catch (err) {
      currentRetry++
      if (currentRetry > maxRetry || !isTransient(err as AxiosError)) {
        ecp.utils.message.error(
          (err as any).response.status + '：' + (err as any).message
        )
        return { status: 500, data: {} } as AxiosResponse<T>
      }

      await delay(currentRetry === 2 ? constants.initialRetryInterval : 30000)
    }
  }
}

const request = axios.create({
  timeout: 10 * 60 * 1000,
  headers: { 'Content-Type': ContentType.form }
})

request.interceptors.request.use(
  (config) => {
    // const token = ''
    // if(token) {
    //   config.headers.token = token
    // }
    return config
  },
  (err) => {
    Promise.reject(err)
  }
)

request.interceptors.response.use((response) => {
  // console.log(response)
  if (response.status !== 200) {
    ecp.utils.message.error(response.data)
  } else {
    const res = response.data
    if (res.code && res.code !== 0 && res.code !== 200) {
      ecp.utils.message.error(`${res.code}：${res.message}`)
      const loginStore = useLoginStore()
      if (res.code === 10502 || res.code === 10503) {
        // 10501: 用户名或密码错误
        // 10502: 用户名不存在
        // 10503: token校验失败
        loginStore.loginOut()
      }
    }
  }
  return response
})

export default {
  get<T = ApiResponse>(
    url: string,
    {
      config,
      maxRetry,
      query
    }: {
      config?: AxiosRequestConfig
      maxRetry?: number
      query?: Record<string, unknown>
    } = {}
  ) {
    if (query && !isEmpty(query)) {
      url += '?' + qs.stringify(query, { indices: false })
    }
    return sendRequestWithAutoRetry(() => request.get<T>(url, config), maxRetry)
  },
  post<T = ApiResponse>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig,
    { maxRetry }: { maxRetry?: number } = {}
  ) {
    return sendRequestWithAutoRetry(
      () => request.post<T>(url, data, config),
      maxRetry
    )
  },
  postJson<T = ApiResponse>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig,
    { maxRetry }: { maxRetry?: number } = {}
  ) {
    config = merge(
      {
        headers: {
          'Content-Type': ContentType.json
        }
      },
      config
    )
    return sendRequestWithAutoRetry(
      () => request.post<T>(url, data, config),
      maxRetry
    )
  }
}
